import { useDebounceFn, useScroll } from '@vueuse/core'
import {
  computed,
  onUnmounted,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
  type StyleValue,
} from 'vue'

export type ScrollContainerOptions = {
  /**
   * Size of the fade gradients
   */
  fadeSize: 'md' | 'lg' | 'xl' | 'xxl'
  /**
   * Distance in pixels over which the fade effect transitions
   */
  fadeDistance: number
  /**
   * Placement of the fade effect. 'both' shows fade on both ends, 'start' only at the beginning,
   * 'end' only at the end. Defaults to 'both'.
   */
  fadePlacement?: 'both' | 'start' | 'end'
}

/**
 * Returns computed styles that can be applied to a scroll container to create a fade effect when
 * scrolling.
 * @param element - Ref to the scroll container element
 * @param options - Options for the fade effect
 * @param mutationObserverOptions - Options for the mutation observer. This might be needed if
 * the container will have content added to it dynamically. In this case, the VueUse useScroll
 * function won't detect changes to the element's scrollHeight, so we need to use a mutation
 * observer. Be careful when using this as it can be inefficient if permissive options are used.
 * @returns Computed styles that can be applied to the scroll container
 */
export const useScrollGradient = (
  element: Ref<HTMLElement | null | undefined>,
  options: MaybeRefOrGetter<ScrollContainerOptions>,
  mutationObserverOptions?: MutationObserverInit,
) => {
  const { y: scrollY, arrivedState, measure } = useScroll(element)

  const debouncedMeasure = useDebounceFn(measure, 250)
  const observer = new MutationObserver(debouncedMeasure)
  watch(element, (newEl) => {
    if (!mutationObserverOptions) {
      return
    }

    if (newEl) {
      observer.observe(newEl, mutationObserverOptions)
    } else {
      observer.disconnect()
    }
  })
  onUnmounted(() => observer.disconnect())

  const FADE_SIZE_VALUES = {
    md: 40,
    lg: 80,
    xl: 160,
    xxl: 320,
  } as const

  const fadeSizeValue = computed(() => FADE_SIZE_VALUES[toValue(options).fadeSize])

  /** Calculate when to show fade indicators */
  const topFade = computed(() => {
    const placement = toValue(options).fadePlacement || 'both'
    if (placement === 'end') return 0
    const scrolled = scrollY.value
    return Math.min(scrolled / toValue(options).fadeDistance, 1)
  })

  const bottomFade = computed(() => {
    const placement = toValue(options).fadePlacement || 'both'
    if (placement === 'start') return 0
    if (!element.value || arrivedState.bottom) return 0
    const { scrollHeight, clientHeight } = element.value
    const maxScroll = scrollHeight - clientHeight
    const scrolledFromBottom = maxScroll - scrollY.value

    return Math.min(scrolledFromBottom / toValue(options).fadeDistance, 1)
  })

  const maskStyle = computed<StyleValue>(() => {
    const fadeSize = fadeSizeValue.value
    const top = topFade.value
    const bottom = bottomFade.value

    const topStop = top > 0 ? `transparent 0px, black ${fadeSize * top}px` : `black 0px`
    const bottomStop =
      bottom > 0 ? `black calc(100% - ${fadeSize * bottom}px), transparent 100%` : `black 100%`

    // Create mask that fades only the edges when scrollable
    return {
      mask: `linear-gradient(to bottom, ${topStop}, black, ${bottomStop})`,
    }
  })

  return maskStyle
}

/**
 * Horizontal scroll gradient composable for x-axis fade effect.
 * Returns computed styles that can be applied to a scroll container to create a horizontal fade effect when scrolling.
 * @param element - Ref to the scroll container element
 * @param options - Options for the fade effect
 * @returns Computed styles that can be applied to the scroll container
 */
export const useScrollXGradient = (
  element: Ref<HTMLElement | null | undefined>,
  options: MaybeRefOrGetter<ScrollContainerOptions>,
  _mutationObserverOptions?: MutationObserverInit,
) => {
  // Use the same logic as useScrollGradient, but for horizontal scroll
  const { x: scrollX, arrivedState } = useScroll(element)

  const FADE_SIZE_VALUES = {
    md: 40,
    lg: 80,
    xl: 160,
    xxl: 320,
  } as const

  const fadeSizeValue = computed(() => FADE_SIZE_VALUES[toValue(options).fadeSize])

  const leftFade = computed(() => {
    const placement = toValue(options).fadePlacement || 'both'
    if (placement === 'end') return 0
    const scrolled = scrollX.value
    return Math.min(scrolled / toValue(options).fadeDistance, 1)
  })

  const rightFade = computed(() => {
    const placement = toValue(options).fadePlacement || 'both'
    if (placement === 'start') return 0
    if (!element.value || arrivedState.right) return 0
    const { scrollWidth, clientWidth } = element.value
    const maxScroll = scrollWidth - clientWidth
    const scrolledFromRight = maxScroll - scrollX.value
    return Math.min(scrolledFromRight / toValue(options).fadeDistance, 1)
  })

  const maskStyle = computed<StyleValue>(() => {
    const fadeSize = fadeSizeValue.value
    const left = leftFade.value
    const right = rightFade.value

    const leftStop = left > 0 ? `transparent 0px, black ${fadeSize * left}px` : `black 0px`
    const rightStop =
      right > 0 ? `black calc(100% - ${fadeSize * right}px), transparent 100%` : `black 100%`

    // Create mask that fades only the left/right edges when scrollable
    return {
      mask: `linear-gradient(to right, ${leftStop}, black, ${rightStop})`,
    }
  })

  return maskStyle
}
